/**
 * Renders a structured legal document (lib/legal-copy.ts). Shared by the
 * signup consent gate (step 3) and the public /terms + /privacy pages, so the
 * text a prospect reads on the marketing site is character-for-character the
 * text the wizard records acceptance against.
 */
import type { LegalDocument } from '../lib/legal-copy';

/**
 * Kebab-case anchor id for a section heading, prefixed per document so the
 * ToS and DPA (which both number their sections from 1) never collide:
 * `sectionAnchorId('tos', '1. The service')` → `"tos-1-the-service"`.
 */
export function sectionAnchorId(prefix: string, heading: string): string {
  const slug = heading
    .toLowerCase()
    .replaceAll(/[^a-z0-9]+/g, '-')
    .replaceAll(/^-+|-+$/g, '');
  return `${prefix}-${slug}`;
}

export function LegalDocumentView({
  document: legalDocument,
  version,
  titleAs = 'h3',
  anchorPrefix,
}: {
  document: LegalDocument;
  /** The authoritative consent version (from `GET /signup/v1`), when known. */
  version?: string;
  /**
   * How to render the document title. The signup consent gate keeps the
   * default 'h3'. The public legal pages render their own page H1, so they
   * pass 'none' for the primary document — and 'h2' for the DPA on /terms,
   * which draws a document break between the ToS and the addendum.
   */
  titleAs?: 'h2' | 'h3' | 'none';
  /** When set, each section gets an id anchor (e.g. "tos-1-the-service") for TOC links. */
  anchorPrefix?: string;
}): JSX.Element {
  const versionSuffix = version === undefined ? null : <span className="muted"> v{version}</span>;
  const isDocumentBreak = titleAs === 'h2' && legalDocument.dividerLabel !== undefined;
  return (
    <section className={`legal-doc${isDocumentBreak ? ' legal-doc--addendum' : ''}`}>
      {titleAs === 'none' ? null : titleAs === 'h2' ? (
        <header className="legal-doc__break">
          {legalDocument.dividerLabel === undefined ? null : (
            <span className="legal-doc__break-label">{legalDocument.dividerLabel}</span>
          )}
          <h2>
            {legalDocument.title}
            {versionSuffix}
          </h2>
        </header>
      ) : (
        <h3>
          {legalDocument.title}
          {versionSuffix}
        </h3>
      )}
      <p className="legal-doc__intro">{legalDocument.intro}</p>
      {legalDocument.sections.map((section) => (
        <div
          className="legal-doc__section"
          key={section.heading}
          id={
            anchorPrefix === undefined ? undefined : sectionAnchorId(anchorPrefix, section.heading)
          }
        >
          <h3>{section.heading}</h3>
          {section.paragraphs?.map((paragraph) => (
            <p key={paragraph.slice(0, 32)}>{paragraph}</p>
          ))}
          {section.bullets ? (
            <ul>
              {section.bullets.map((bullet) => (
                <li key={bullet.slice(0, 32)}>{bullet}</li>
              ))}
            </ul>
          ) : null}
        </div>
      ))}
    </section>
  );
}
